import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { getOsmNames, getData, updateDashboardConfig, DashboardConfig } from '../../services/api.ts';
import { subtractMinutes, nowISOString } from '../../utils/utils.ts';

type SensorRow = {
  id:            number;
  unit_id:       string;
  serial_number: string;
  osm_name:      string;
  description:   string | null;
  online:        boolean | null;
};

const headCellSx = {
  fontFamily:    "'Syne', sans-serif",
  fontSize:      11,
  fontWeight:    600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color:         '#ffffff !important',
  background:    '#1D9E75 !important',
  borderBottom:  'none',
  py:            1.25,
};

const bodyCellSx = {
  fontFamily:   "'DM Mono', monospace",
  fontSize:     13,
  color:        'var(--color-text-primary)',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
  py:           1.25,
  background:   'transparent',
};

function StatusDot({ online }: { online: boolean | null }) {
  if (online === null) return <CircularProgress size={12} sx={{ color: '#1D9E75' }} />;
  return (
    <Box sx={{
      display:      'inline-block',
      width:        10,
      height:       10,
      borderRadius: '50%',
      background:   online ? '#1D9E75' : '#993C1D',
      boxShadow:    online
        ? '0 0 6px rgba(29,158,117,0.7)'
        : '0 0 6px rgba(153,60,29,0.5)',
    }} />
  );
}

type Props = { isAdmin: boolean };

export default function SensorStatusTable({ isAdmin }: Props) {
  const [rows, setRows]               = React.useState<SensorRow[]>([]);
  const [loading, setLoading]         = React.useState(true);
  const [error, setError]             = React.useState<string | null>(null);
  const [editingId, setEditingId]     = React.useState<number | null>(null);
  const [editingDesc, setEditingDesc] = React.useState<string>('');

  React.useEffect(() => {
    async function load() {
      try {
        const configs: DashboardConfig[] = await getOsmNames();

        setRows(configs.map(c => ({
          id:            c.id,
          unit_id:       c.unit_id,
          serial_number: c.serial_number,
          osm_name:      c.osm_name,
          description:   c.description,
          online:        null,
        })));
        setLoading(false);

        const now       = nowISOString();
        const thirtyAgo = subtractMinutes('30');

        await Promise.all(
          configs.map(async (c, i) => {
            try {
              const data   = await getData('pg_sf_ds', 'bucket', thirtyAgo, now, 'SND', c.osm_name);
              const online = Array.isArray(data) && data.length > 0;
              setRows(prev => prev.map((r, idx) => idx === i ? { ...r, online } : r));
            } catch {
              setRows(prev => prev.map((r, idx) => idx === i ? { ...r, online: false } : r));
            }
          })
        );
      } catch (err: any) {
        setError(err.message);
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleDescriptionSave(row: SensorRow) {
    if (editingDesc === (row.description ?? '')) { setEditingId(null); return; }
    try {
      await updateDashboardConfig(row.id, { description: editingDesc });
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, description: editingDesc } : r));
    } catch (err: any) {
      console.error('Failed to update description:', err.message);
    } finally {
      setEditingId(null);
    }
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
      <CircularProgress size={24} sx={{ color: '#1D9E75' }} />
    </Box>
  );

  if (error) return (
    <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#993C1D' }}>
      Error: {error}
    </Typography>
  );

  const onlineCount  = rows.filter(r => r.online === true).length;
  const offlineCount = rows.filter(r => r.online === false).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Summary badges */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75' }} />
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--color-text-secondary)' }}>
            {onlineCount} online
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#993C1D' }} />
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--color-text-secondary)' }}>
            {offlineCount} offline
          </Typography>
        </Box>
      </Box>

      <TableContainer sx={{ background: 'transparent' }}>
        <Table aria-label="sensor status table" size="small">
          <TableHead>
            <TableRow>
              <TableCell align="left"   sx={headCellSx}>Unit ID</TableCell>
              <TableCell align="left"   sx={headCellSx}>Serial Number</TableCell>
              <TableCell align="left"   sx={headCellSx}>Location</TableCell>
              <TableCell align="left"   sx={headCellSx}>
                Description {isAdmin && (
                  <Typography component="span" sx={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: 'var(--color-text-secondary)', ml: 0.5 }}>
                    (click to edit)
                  </Typography>
                )}
              </TableCell>
              <TableCell align="center" sx={headCellSx}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.serial_number} sx={{ '&:last-child td': { border: 0 } }}>

                <TableCell align="left" sx={{ ...bodyCellSx, color: 'var(--color-text-secondary)' }}>
                  {row.unit_id}
                </TableCell>

                <TableCell align="left" sx={{ ...bodyCellSx, color: 'var(--color-text-secondary)' }}>
                  {row.serial_number}
                </TableCell>

                <TableCell align="left" sx={{ ...bodyCellSx, fontFamily: "'Syne', sans-serif", fontWeight: 500, fontSize: 13 }}>
                  {row.osm_name}
                </TableCell>

                <TableCell align="left" sx={bodyCellSx}>
                  {editingId === row.id ? (
                    <input
                      autoFocus
                      value={editingDesc}
                      onChange={e => setEditingDesc(e.target.value)}
                      onBlur={() => handleDescriptionSave(row)}
                      onKeyDown={e => {
                        if (e.key === 'Enter')  handleDescriptionSave(row);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      style={{
                        width:       '100%',
                        background:  'transparent',
                        border:      'none',
                        borderBottom: '1px solid #1D9E75',
                        color:       'var(--color-text-primary)',
                        fontFamily:  "'DM Mono', monospace",
                        fontSize:    13,
                        outline:     'none',
                        padding:     '2px 0',
                      }}
                    />
                  ) : (
                    <span
                      onClick={isAdmin ? () => { setEditingId(row.id); setEditingDesc(row.description ?? ''); } : undefined}
                      title={isAdmin ? 'Click to edit' : undefined}
                      style={{
                        cursor:    isAdmin ? 'pointer' : 'default',
                        display:   'block',
                        minHeight: '1.5em',
                        color:     row.description ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                      }}
                    >
                      {row.description ?? '—'}
                    </span>
                  )}
                </TableCell>

                <TableCell align="center" sx={bodyCellSx}>
                  <StatusDot online={row.online} />
                </TableCell>

              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
